import {useLingui} from '@lingui/react/macro';
import {useCallback} from 'react';
import {useToast} from 'react-native-toast-notifications';
import {WebFrame} from 'interface/base/WebFrame';
import {delay} from 'utils/common/time';
import {isNative, getHost} from 'utils/platform';
import {sessionToken, copyLink, privacy, trackShare, isFolderkey, isQuickkey, getKeysFromUrl} from 'utils/mediafire';
import device from 'extensions/device';
import {useModal} from 'extensions/viewport/useModal';
import {WebViewMessageEvent} from 'react-native-webview';

export function useModalShare(ids: string[], name?: string, url?: string, oneTimeLink?: boolean, isPrivate?: boolean) {
  const {t} = useLingui();
  let folders = ids.filter(id => isFolderkey(id));
  let files = ids.filter(id => isQuickkey(id));

  // No valid ids found, use key from url (file upload)
  if (url && folders.length === 0 && files.length === 0) {
    const keys = getKeysFromUrl(url);
    folders = keys.filter(id => isFolderkey(id));
    files = keys.filter(id => isQuickkey(id));
  }

  const toast = useToast();
  const modal = useModal();

  const exit = useCallback(() => modal.exit(), []);

  const message = useCallback((e: WebViewMessageEvent) => {
    if (e && e.nativeEvent.data === 'mf-close-dialogs') {
      exit();
    } else if(e && typeof e.nativeEvent.data === 'string' && e.nativeEvent.data.indexOf('mf-share-') !== -1) {
      copyLink(e.nativeEvent.data.slice(9));
      toast.show(t`Copied share link to clipboard`);
      exit();
    } else if(e && typeof e.nativeEvent.data === 'string' && e.nativeEvent.data.indexOf('mf-open-') !== -1) {
      window.open(e.nativeEvent.data.slice(8), '_blank');
    }
  }, [ids]);

  const show = useCallback(async () => {
    if (isPrivate && !oneTimeLink) {
      await privacy(ids, 'public');
      toast.show(t`Warning: Item was private, but has now been made public.`, {type: 'danger', duration: 10000});
      await delay(2000);
    }

    if (isNative() && !oneTimeLink) {
      device.share(url, name);
      exit();
    } else {
      const auth = sessionToken ? `&session_token=${sessionToken}` : '';
      const otdl = oneTimeLink ? '&1tdl=1' : '';
      const base = `${getHost()}/widgets/share.php?web=1&dlr=1${otdl}${auth}`;
      const path = `${base}&folders=${folders.join(',')}&files=${files.join(',')}`;
      modal.open(
        <WebFrame url={path} onMessage={message} exit={exit}/>,
        {frameless: true, overlay: true},
      );
    }
    trackShare();
  }, [ids, isPrivate, oneTimeLink, sessionToken]);

  return [show, exit];
}
